import React, { useState, useRef, useEffect } from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AddIcon from '@mui/icons-material/Add';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import { TCourseViewModel, TTreeItemDataViewModel, TTreeViewModel } from '../../../services/models/CourseViewModels';
import Title from '../../titleAndInput/Title';
import { createUseStyles } from 'react-jss';
import PageStatus from '../../../features/courseEdit/pageStatusIndicator/PageStatus';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useStore } from '../../../store/store';
import { observer } from 'mobx-react';
import { useSnackbar } from 'notistack';
import Popper from '@mui/material/Popper/Popper';
import Paper from '@mui/material/Paper/Paper';
import Typography from '@mui/material/Typography/Typography';
import TextField from '@mui/material/TextField/TextField';
import Button from '@mui/material/Button/Button';
import Tree from 'antd/es/tree/Tree';
import { convertToAntdTree } from '../../../utils/pages/convertToAntdTree';
import { IPageItem } from '../../../routes/courseDashboard/features/coursePages/CoursePageList';
import PageNavigationItem from './PageNavigationItem';
import { useQueryParam, NumberParam } from 'use-query-params';

interface PageNavigationProps {
  courseID: number;
}

const useStyles = createUseStyles((theme: Theme) => ({
  paperTest: {
    minWidth: 500,
    borderRadius: 0,
    background: theme.palette.bgOne.main,
    color: theme.palette.bgOne.contrastText,
  },
  title: {
    fontSize: 20,
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  tooltip: {
    fontSize: '0.9em',
  },
  courseListTree: {
    '& .ant-tree-node-content-wrapper:hover, \
       & .ant-tree-node-content-wrapper.ant-tree-node-selected, \
       & .ant-tree-node-content-wrapper:focus, \
       & .ant-tree-node-content-wrapper:active': {
      backgroundColor: 'transparent !important',
      boxShadow: 'none',
    },

    '& .ant-tree-treenode:hover': {
      backgroundColor: theme.palette.primary.light,
    },

    '& .ant-tree-title div': {
      display: 'flex',
      alignItems: 'center',
      padding: 6,
    },
    '& .ant-tree-treenode:hover .ant-tree-title': {
      color: theme.palette.primary.contrastText,
    },
    '& .ant-tree-treenode:has(> .ant-tree-node-content-wrapper.ant-tree-node-selected)': {
      backgroundColor: theme.palette.primary.main,
    },
    '& .ant-tree-node-content-wrapper.ant-tree-node-selected': {
      backgroundColor: 'transparent',
      color: theme.palette.primary.contrastText,
    },

    '& .ant-tree-indent-unit': {
      width: 24,
    },

    '& .ant-tree-switcher': {
      width: 12,
    },
  },
}));

export default observer(function PageNavigation({ courseID }: PageNavigationProps) {
  const { courseStore, pageStore } = useStore();
  const { newEmptyPage, courseViewModel, loadCourseViewModel } = courseStore;
  const { coursePage, getCoursePage, openPagePreview } = pageStore;
  const [treeData, setTreeData] = useState<IPageItem[]>(courseViewModel ? convertToAntdTree(courseViewModel.pages, false) : []);
  const theme = useTheme();
  const classes = useStyles({ theme });
  const divRef = useRef<HTMLDivElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState<TTreeViewModel>();
  const [nextPage, setNextPage] = useState<number>();
  const [previousPage, setPreviousPage] = useState<number>();
  const [pageIDParam, setPageIDParam] = useQueryParam('pageID', NumberParam);

  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [popperOpen, setPopperOpen] = React.useState(false);
  const [popperAnchorEl, setPopperAnchorEl] = React.useState<null | HTMLElement>(null);
  const [newName, setNewName] = useState('');

  const handlePreviousPage = () => {
    console.log('previousPage', previousPage);
    setPageIDParam(previousPage);
  };
  const handleNextPage = () => {
    console.log('nextPage', nextPage);
    setPageIDParam(nextPage);
  };

  const handleTogglePopover = () => setOpen(!open);
  const handleClosePopover = () => setOpen(false);

  const createLoadNextPage = async () => {
    if (courseViewModel !== undefined) {
      handleClosePopper();

      const page = await newEmptyPage(Object.keys(courseViewModel.pages).length + 1, undefined, newName.length > 0 ? newName : undefined);
      setNewName('');
      if (page !== undefined) {
        setPageIDParam(page.coursePageID);
      }
    }
  };

  const emptyFunc = () => {
    return;
  };

  const popperHandleClick = () => {
    setPopperAnchorEl(anchorRef.current);
    setPopperOpen(true);
  };

  const handleOnClickNewPage = (e: any) => {
    if (e.keyCode === 13) {
      createLoadNextPage();
    }
  };

  const handleClosePopper = () => {
    setPopperOpen(false);
  };

  useEffect(() => {
    const loadNavigation = (aCourseViewModel: TCourseViewModel) => {
      console.log('coursePage', coursePage);
      if (coursePage) {
        const tmpCurrentPage = findByIdInPages(aCourseViewModel.pages, String(coursePage.coursePageID));
        if (tmpCurrentPage) {
          setCurrentPage(tmpCurrentPage);
          setNextPage(coursePage.nextPage?.coursePageID);
          setPreviousPage(coursePage.prevPage?.coursePageID);
        }
      }
    };

    if (!courseViewModel) {
      loadCourseViewModel(courseID).then((aCourseViewModel) => {
        if (aCourseViewModel) {
          loadNavigation(aCourseViewModel);
          setTreeData(convertToAntdTree(aCourseViewModel.pages, false));
        }
      });
    } else {
      loadNavigation(courseViewModel);
    }
  }, [coursePage]);

  useEffect(() => {
    const handlePageUpDown = (e: any) => {
      if (e.keyCode == 33) {
        //Previous page
        if (previousPage) {
          handlePreviousPage();
        } else {
          enqueueSnackbar('You are on the first page', { variant: 'info' });
        }
      } else if (e.keyCode == 34) {
        //Next page
        if (nextPage) {
          handleNextPage();
        } else {
          enqueueSnackbar('You are on the last page', { variant: 'info' });
        }
      }
    };

    window.addEventListener('keydown', handlePageUpDown, { passive: false });
    return () => {
      window.removeEventListener('keydown', handlePageUpDown);
    };
  }, [previousPage, nextPage]);

  /*   useEffect(() => {
    if (courseViewModel) {
      setTreeData(convertToAntdTree(courseViewModel.pages, false));
      if (currentPage) {
        setCurrentPageID(currentPage.id);
      }
    }
  }, [courseViewModel, currentPage]); */

  return (
    <>
      <div style={{ display: 'flex', flexGrow: 1, justifyContent: 'center', marginRight: 24 }}>
        <div style={{ minWidth: 35, alignSelf: 'end' }}>
          <Tooltip title='Previous Page'>
            <div>
              <IconButton disabled={previousPage === undefined} size='small' onClick={handlePreviousPage}>
                <ArrowBackIosNewIcon />
              </IconButton>
            </div>
          </Tooltip>
        </div>
        <div ref={divRef} style={{ position: 'relative', display: 'flex', justifyContent: 'space-around', alignItems: 'center', width: 500, marginLeft: 20, marginRight: 20 }}>
          {currentPage !== undefined && (
            <>
              <PageStatus />
              <Title data={currentPage.data} className={classes.title} topBar />
              <Tooltip title='Page List'>
                <IconButton size='small' style={{ color: 'inherit' }} onClick={handleTogglePopover}>
                  {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
              </Tooltip>
            </>
          )}
          <Popover
            open={open}
            anchorEl={divRef.current}
            onClose={handleClosePopover}
            slotProps={{ paper: { className: classes.paperTest } }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            {/* {courseViewModel && (
              <CourseView
                pageID={pageIDParam ? pageIDParam : 0}
                pages={courseViewModel.pages}
                height={500}
                compact
                handleClose={handleClosePopover}
                handleNewPageItem={emptyFunc}
                handleCopyPageItem={emptyFunc}
                handlePastePage={emptyFunc}
                handleDuplicatePageItem={emptyFunc}
              />
            )} */}
            <div className={classes.courseListTree}>
              <Tree
                treeData={treeData}
                blockNode
                defaultExpandAll
                switcherIcon={() => null}
                expandAction={false}
                onExpand={() => {
                  /* no-op: we don't allow collapsing */
                }}
                selectable={true} // disables user selection clicks
                selectedKeys={[pageIDParam ?? 0]}
                height={500}
                titleRender={(node) => {
                  return <PageNavigationItem nodeData={node as IPageItem} onClick={() => handleTogglePopover()} />;
                }}
                rootStyle={{ backgroundColor: 'transparent' }}
              />
            </div>
          </Popover>
        </div>
        <div ref={anchorRef} style={{ minWidth: 35 }}>
          {nextPage !== undefined ? (
            <Tooltip title='Next Page'>
              <IconButton size='small' onClick={handleNextPage}>
                <ArrowForwardIosIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title='Create New Page' classes={{ tooltip: classes.tooltip }}>
              <IconButton size='small' onClick={popperHandleClick}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
      <Popper
        open={popperOpen}
        anchorEl={popperAnchorEl}
        placement='top'
        sx={{
          zIndex: 10000,
        }}
      >
        <Paper elevation={5} sx={{ background: theme.palette.bgFour.main, padding: theme.spacing(2) }}>
          <Typography variant='h6'>Page Name</Typography>
          <TextField variant={'standard'} placeholder={'New Page'} value={newName} onChange={(e) => setNewName(e.target.value)} fullWidth onKeyUp={(e: any) => handleOnClickNewPage(e)} />
          <div style={{ marginTop: theme.spacing(2), display: 'flex', justifyContent: 'end' }}>
            <Button onClick={handleClosePopper} variant='newSecondaryText'>
              Cancel
            </Button>
            <Button onClick={createLoadNextPage} variant='contained' color='primary'>
              OK
            </Button>
          </div>
        </Paper>
      </Popper>
    </>
  );
});

export function findByIdInPages(pages: TTreeViewModel[], id: string): TTreeViewModel | undefined {
  // Iterative DFS avoids deep recursion limits
  const stack: TTreeViewModel[] = [...pages];
  while (stack.length) {
    const node = stack.pop()!;
    if (node.id === id) return node;
    if (node.children?.length) stack.push(...node.children);
  }
  return undefined;
}
